import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { callEdgeFunction } from '@/lib/edge-functions'
import { useResetEmailCooldown } from '@/hooks/use-reset-email-cooldown'
import {
  getResetEmailErrorMessage,
  getSuggestedResetEmailCooldownSeconds,
  isAuthRateLimitError,
} from '@/lib/auth-rate-limit'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { AlertCircle, CheckCircle, Loader2 } from 'lucide-react'

export type AuthView = 'signin' | 'signup' | 'forgot'

interface AuthFormProps {
  initialView?: AuthView
  redirectTo?: string
  inviteToken?: string | null
  inviteEmail?: string | null
  authError?: string | null
  onViewChange?: (view: AuthView) => void
  onSuccess?: () => void
}

function normalizeRedirectPath(path?: string) {
  if (!path || !path.startsWith('/')) {
    return '/'
  }

  return path
}

function getDefaultDestination(redirectPath: string, isAdmin: boolean) {
  if (isAdmin) {
    if (redirectPath === '/' || redirectPath.startsWith('/admin')) {
      return '/admin'
    }

    return redirectPath
  }

  if (redirectPath === '/' || redirectPath.startsWith('/admin')) {
    return '/account'
  }

  return redirectPath
}

function getRouteErrorMessage(authError?: string | null) {
  if (!authError) {
    return null
  }

  if (authError === 'unauthorized') {
    return 'You need to log in to access this page.'
  }

  if (authError === 'auth_callback_error') {
    return 'Sign in could not be completed. Please try again.'
  }

  return null
}

export function AuthForm({
  initialView = 'signin',
  redirectTo,
  inviteToken = null,
  inviteEmail = null,
  authError = null,
  onViewChange,
  onSuccess,
}: AuthFormProps) {
  const [view, setView] = useState<AuthView>(initialView)
  const [email, setEmail] = useState(inviteEmail || '')
  const [password, setPassword] = useState('')
  const [name, setName] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [pendingConfirmationEmail, setPendingConfirmationEmail] = useState<string | null>(null)
  const [isResendLoading, setIsResendLoading] = useState(false)
  const [resendSuccess, setResendSuccess] = useState(false)
  const [isResetEmailSent, setIsResetEmailSent] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const navigate = useNavigate()
  const {
    remainingSeconds: resetCooldownSeconds,
    isCoolingDown: isResetCooldownActive,
    startCooldown: startResetCooldown,
  } = useResetEmailCooldown()

  useEffect(() => {
    setView(initialView)
    setError(null)
    setPendingConfirmationEmail(null)
    setIsResendLoading(false)
    setResendSuccess(false)
    setIsResetEmailSent(false)
  }, [initialView])

  const resolvedRedirect = normalizeRedirectPath(redirectTo)
  const routeError = getRouteErrorMessage(authError)

  const isLogin = view === 'signin'
  const isSignup = view === 'signup'
  const isForgotPassword = view === 'forgot'
  const isSignupConfirmation = !!pendingConfirmationEmail
  const activeError = error || (isLogin ? routeError : null)

  const title = isLogin
    ? 'Welcome back'
    : isSignup
      ? isSignupConfirmation
        ? 'Confirm your email'
        : 'Create an account'
      : isResetEmailSent
        ? 'Check your email'
        : 'Forgot password?'

  const description = isLogin
    ? 'Sign in to your account to continue'
    : isSignup
      ? isSignupConfirmation
        ? `We sent a confirmation link to ${pendingConfirmationEmail}`
        : 'Enter your details to create your account'
      : isResetEmailSent
        ? `We sent a password reset link to ${email}`
        : 'Enter your email and we will send you a reset link'

  const buttonText = isLogin
    ? 'Sign In'
    : isSignup
      ? 'Create Account'
      : isResetCooldownActive
        ? `Try again in ${resetCooldownSeconds}s`
        : 'Send reset link'

  const loadingText = isLogin
    ? 'Signing in...'
    : isSignup
      ? 'Creating account...'
      : 'Sending reset link...'

  const handleViewChange = (nextView: AuthView) => {
    setError(null)
    setPendingConfirmationEmail(null)
    setIsResendLoading(false)
    setResendSuccess(false)
    setIsResetEmailSent(false)
    setView(nextView)
    onViewChange?.(nextView)
  }

  const handleResendConfirmation = async () => {
    if (!pendingConfirmationEmail) {
      return
    }

    setError(null)
    setResendSuccess(false)
    setIsResendLoading(true)

    try {
      const { error: resendError } = await supabase.auth.resend({
        type: 'signup',
        email: pendingConfirmationEmail,
        options: {
          emailRedirectTo: `${window.location.origin}/auth/callback`,
        },
      })

      if (resendError) {
        setError(resendError.message)
        return
      }

      setResendSuccess(true)
    } catch {
      setError('Failed to resend confirmation email')
    } finally {
      setIsResendLoading(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (isForgotPassword && isResetCooldownActive) {
      setError(`Please wait ${resetCooldownSeconds} seconds before requesting another reset link.`)
      return
    }

    setError(null)
    setResendSuccess(false)
    setIsLoading(true)

    try {
      if (isLogin) {
        const { error: authError } = await supabase.auth.signInWithPassword({
          email,
          password,
        })

        if (authError) {
          setError(authError.message)
          return
        }

        const { data: { user } } = await supabase.auth.getUser()
        if (user) {
          // Use maybeSingle to avoid 406 error for non-admin users
          const { data: adminUser } = await supabase
            .from('admin_users')
            .select('id')
            .eq('id', user.id)
            .maybeSingle()

          let destination = getDefaultDestination(resolvedRedirect, !!adminUser)

          if (inviteToken) {
            try {
              const result = await callEdgeFunction('accept-invite', {
                method: 'POST',
                body: { token: inviteToken },
                withAuth: true,
              })

              if (!result.error) {
                destination = '/admin'
              }
            } catch {
              // Invite processing failed, continue to normal flow
            }
          }

          onSuccess?.()
          navigate(destination)
        }

        return
      }

      if (isSignup) {
        // Invite flow: skip signUp() entirely — accept-invite creates the user server-side
        // via Admin API with email already confirmed (no verification email sent)
        if (inviteToken) {
          try {
            const result = await callEdgeFunction('accept-invite', {
              method: 'POST',
              body: { token: inviteToken, email, password, name },
            })

            if (result.error) {
              setError(result.error)
              return
            }

            // User created server-side with confirmed email — now sign in
            const { error: signInError } = await supabase.auth.signInWithPassword({
              email,
              password,
            })

            if (signInError) {
              setError(signInError.message)
              return
            }

            onSuccess?.()
            navigate('/admin')
            return
          } catch {
            setError('Failed to accept invitation. Please try again.')
            return
          }
        }

        // Normal signup (no invite) — use standard signUp flow
        const emailRedirectTo = `${window.location.origin}/auth/callback`
        const { data: signUpData, error: authError } = await supabase.auth.signUp({
          email,
          password,
          options: {
            data: {
              display_name: name,
            },
            emailRedirectTo,
          },
        })

        if (authError) {
          setError(authError.message)
          return
        }

        if (!signUpData.user) {
          return
        }

        // Check if we have a confirmed session
        const { data: { user: sessionUser } } = await supabase.auth.getUser()

        if (!sessionUser) {
          // Normal signup — show confirmation screen
          setPendingConfirmationEmail(signUpData.user.email ?? email)
          return
        }

        // User has a confirmed session (e.g., auto-confirm is on)
        const { error: profileError } = await supabase
          .from('customer_profiles')
          .upsert(
            {
              id: sessionUser.id,
              email: sessionUser.email!,
              display_name: name || null,
            },
            { onConflict: 'id' }
          )

        if (profileError) {
          // Non-critical: profile can be created later in account pages or callback flow.
        }

        try {
          await callEdgeFunction('claim-orders', {
            method: 'POST',
            withAuth: true,
          })
        } catch {
          // Non-critical, don't block
        }

        onSuccess?.()
        navigate(getDefaultDestination(resolvedRedirect, false))
        return
      }

      const { error: resetError } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/auth/reset-password`,
      })

      if (resetError) {
        if (isAuthRateLimitError(resetError)) {
          startResetCooldown(getSuggestedResetEmailCooldownSeconds(resetError))
        }

        setError(getResetEmailErrorMessage(resetError))
        return
      }

      startResetCooldown()
      setIsResetEmailSent(true)
    } catch {
      setError('An unexpected error occurred')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Card className="w-full max-w-md">
      <CardHeader className="text-center">
        <CardTitle className="text-2xl">{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent>
        {activeError && (
          <Alert variant="destructive" className="mb-6">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{activeError}</AlertDescription>
          </Alert>
        )}

        {inviteToken && isSignup && !isSignupConfirmation && (
          <Alert className="mb-6">
            <AlertDescription>
              You've been invited to join the team. Create your account to accept the invitation.
            </AlertDescription>
          </Alert>
        )}

        {inviteToken && isLogin && (
          <Alert className="mb-6">
            <AlertDescription>
              You've been invited to join the team. Sign in to accept your invitation.
            </AlertDescription>
          </Alert>
        )}

        {isSignupConfirmation ? (
          <div className="space-y-4">
            <div className="flex items-center justify-center text-sm text-muted-foreground">
              <CheckCircle className="mr-2 h-4 w-4 text-green-600" />
              Confirmation email sent
            </div>
            <p className="text-sm text-muted-foreground text-center">
              Open your inbox and click the confirmation link to activate your account.
              {inviteToken ? ' Then sign in to accept your invitation.' : ''}
            </p>
            {resendSuccess && (
              <Alert>
                <AlertDescription>
                  Confirmation email resent successfully.
                </AlertDescription>
              </Alert>
            )}
            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={handleResendConfirmation}
              disabled={isResendLoading}
            >
              {isResendLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Resending...
                </>
              ) : (
                'Resend confirmation email'
              )}
            </Button>
            <Button
              type="button"
              className="w-full"
              onClick={() => handleViewChange('signin')}
            >
              Back to sign in
            </Button>
          </div>
        ) : isResetEmailSent ? (
          <div className="space-y-4">
            <div className="flex items-center justify-center text-sm text-muted-foreground">
              <CheckCircle className="mr-2 h-4 w-4 text-green-600" />
              Reset link sent successfully
            </div>
            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={() => setIsResetEmailSent(false)}
            >
              Use a different email
            </Button>
            {isResetCooldownActive && (
              <p className="text-xs text-muted-foreground text-center">
                You can request another reset email in {resetCooldownSeconds} seconds.
              </p>
            )}
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            {isSignup && (
              <div className="space-y-2">
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name"
                  type="text"
                  placeholder="Your name"
                  autoComplete="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  disabled={isLoading}
                />
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@example.com"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={isLoading}
                readOnly={!!inviteEmail}
                className={inviteEmail ? 'bg-muted' : ''}
              />
              {inviteEmail && (
                <p className="text-xs text-muted-foreground">
                  This email matches your invitation and cannot be changed.
                </p>
              )}
            </div>

            {!isForgotPassword && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password">Password</Label>
                  {isLogin && (
                    <button
                      type="button"
                      className="text-sm text-muted-foreground hover:text-foreground"
                      onClick={() => handleViewChange('forgot')}
                    >
                      Forgot password?
                    </button>
                  )}
                </div>
                <Input
                  id="password"
                  type="password"
                  autoComplete={isSignup ? 'new-password' : 'current-password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  disabled={isLoading}
                  minLength={6}
                />
              </div>
            )}

            <Button
              type="submit"
              className="w-full"
              disabled={isLoading || (isForgotPassword && isResetCooldownActive)}
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {loadingText}
                </>
              ) : (
                buttonText
              )}
            </Button>

            {isForgotPassword && isResetCooldownActive && (
              <p className="text-xs text-muted-foreground text-center">
                Please wait {resetCooldownSeconds} seconds before requesting another reset email.
              </p>
            )}
          </form>
        )}
      </CardContent>
      <CardFooter className="flex justify-center">
        {isLogin && !isSignupConfirmation && (
          <p className="text-sm text-muted-foreground">
            Don't have an account?{' '}
            <button
              type="button"
              className="text-foreground hover:underline"
              onClick={() => handleViewChange('signup')}
            >
              Sign up
            </button>
          </p>
        )}

        {(isSignup || isForgotPassword) && !isSignupConfirmation && (
          <p className="text-sm text-muted-foreground">
            Already have an account?{' '}
            <button
              type="button"
              className="text-foreground hover:underline"
              onClick={() => handleViewChange('signin')}
            >
              Sign in
            </button>
          </p>
        )}
      </CardFooter>
    </Card>
  )
}
